import { shareACP } from '@api/resident';
import ImportantNotificationBanner from '@components/ImportantNotificationBanner';
import NewErrorSummary from '@components/NewErrorSummary';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import WarningText from '@components/WarningText';
import { Recipient, SharingACPDetails } from '@typings/ACPTypes';
import { ApiResponse } from '@typings/ApiResponse';
import { Role } from '@typings/ProfileTypes';
import { ResidentDetails } from '@typings/ResidentTypes';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import {
  capitalizeName,
  getErrorMessage as getErrorMessageFromErrors,
  isValidEmail,
  updateErrors,
} from '@utils/commons';
import getErrorMessage from '@utils/errorUtils';
import React, { useEffect, useState } from 'react';
import { SharingACPTypes } from './utils';

export interface NextOfKinDetails {
  fullName: string;
  relationship: string;
}
interface SharingByEmailProps {
  role: string;
  userId: string;
  residentDetails: ResidentDetails | undefined;
  onFormSubmit: () => void;
  nextOfKinDetails: NextOfKinDetails;
  setNextOfKinDetails: (info: NextOfKinDetails) => void;
}

const SharingByEmail: React.FC<SharingByEmailProps> = ({
  role,
  userId,
  residentDetails,
  onFormSubmit,
  nextOfKinDetails,
  setNextOfKinDetails,
}) => {
  const [residentFullName, setResidentFullName] = useState('');
  const [recipientInfo, setRecipientInfo] = useState<Recipient>({
    name: '',
    relationship: '',
    email: '',
  });
  const [errorMessages, setErrorMessages] = useState<{
    fullName: string | undefined;
    email: string | undefined;
    relationship: string | undefined;
  }>({
    fullName: '',
    email: '',
    relationship: '',
  });
  const [assignInfo, setAssignInfo] = useState<{
    hasNextOfKin: boolean;
    isDisabled: boolean;
    isChecked: boolean;
  }>({
    hasNextOfKin: false,
    isDisabled: false,
    isChecked: false,
  });
  const [errors, setErrors] = useState<FormError[]>([]);
  const [sharingByEmailStatus, setSharingByEmailStatus] =
    useState<SubmitStatus>({
      loading: false,
      submitted: SubmitStatusEnum.IDLE,
      message: '',
    });
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const validateEmail = (email: string) => {
    const value = email.trim();
    if (!value) {
      return 'Please enter their email';
    }

    if (!isValidEmail(email)) {
      return "Please enter a valid recipient's email";
    }

    return '';
  };

  useEffect(() => {
    if (residentDetails) {
      const {
        profile: {
          full_name: fullName,
          nfk_name: nfkName,
          nfk_relationship: nfkRelationship,
        },
      } = residentDetails;
      setResidentFullName(fullName);
      setNextOfKinDetails({
        fullName: nfkName || '',
        relationship: nfkRelationship || '',
      });
    }
  }, [residentDetails, setNextOfKinDetails]);

  const handleInputChange = (
    field: 'name' | 'relationship' | 'email',
    value: string
  ) => {
    setRecipientInfo(prevState => ({ ...prevState, [field]: value }));
  };

  const onFinish = async (e: any) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'sbe-full-name',
      !recipientInfo.name ? 'Please enter their full name' : null
    );
    validationErrors = updateErrors(
      validationErrors,
      'sbe-relationship',
      !recipientInfo.relationship.trim()
        ? 'Please enter their relationship'
        : null
    );
    validationErrors = updateErrors(
      validationErrors,
      'sbe-email',
      validateEmail(recipientInfo.email || '')
    );
    setErrors(prev => validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    setSharingByEmailStatus(prev => ({ ...prev, loading: true }));
    try {
      setIsSendingEmail(true);
      const response: ApiResponse<SharingACPDetails> = await shareACP({
        ...recipientInfo,
        next_of_kin: assignInfo.isChecked,
        carehome_user_id: userId,
        type: SharingACPTypes.EMAIL,
      });
      if (response?.status === 'success') {
        setRecipientInfo({ name: '', relationship: '', email: '' });
        onFormSubmit();
        setAssignInfo(prev => ({
          ...prev,
          isChecked: false,
        }));
        setSharingByEmailStatus(prev => ({
          ...prev,
          submitted: SubmitStatusEnum.SUCCESS,
          message: 'This document has been saved and emailed to the recipient',
        }));
        setTimeout(() => {
          setSharingByEmailStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.IDLE,
            message: '',
          }));
        }, 3000);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setSharingByEmailStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });
    } finally {
      setSharingByEmailStatus(prev => ({ ...prev, loading: false }));
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    setErrorMessages({
      fullName: getErrorMessageFromErrors(errors, 'sbe-full-name'),
      email: getErrorMessageFromErrors(errors, 'sbe-email'),
      relationship: getErrorMessageFromErrors(errors, 'sbe-relationship'),
    });
  }, [errors]);

  const handleCheckboxChange = async (e: any) => {
    const { checked } = e.target;

    if (checked) {
      if (assignInfo.hasNextOfKin) {
        setRecipientInfo({
          ...recipientInfo,
          name: nextOfKinDetails.fullName || recipientInfo.name,
          relationship:
            nextOfKinDetails.relationship || recipientInfo.relationship,
        });
        setAssignInfo({ ...assignInfo, isChecked: checked });
      } else {
        setAssignInfo(prev => ({ ...prev, isChecked: true }));
        setTimeout(() => {
          setAssignInfo(prev => ({
            ...prev,
            isChecked: false,
            isDisabled: true,
          }));
        }, 500);
      }
    } else if (!checked) {
      setAssignInfo(prev => ({
        ...prev,
        isChecked: false,
      }));
      if (nextOfKinDetails.fullName || nextOfKinDetails.relationship) {
        setRecipientInfo({
          ...recipientInfo,
          name: '',
          relationship: '',
        });
      }
    }
  };

  useEffect(() => {
    setAssignInfo({
      ...assignInfo,
      hasNextOfKin:
        !!nextOfKinDetails.fullName && !!nextOfKinDetails.relationship,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextOfKinDetails]);

  return (
    <div className="mb-10">
      <h1 className="govuk-heading-l mt-10 govuk-bp:mt-14">Sharing by Email</h1>
      {isSendingEmail && (
        <ImportantNotificationBanner
          title="We are saving and emailing the document"
          duration={10000}
          noScrollToTop
        />
      )}
      {sharingByEmailStatus.submitted === SubmitStatusEnum.SUCCESS && (
        <SuccessNotificationBanner
          title={sharingByEmailStatus.message}
          duration={3000}
          noScrollToTop
        />
      )}
      <p className="govuk-body">
        Enter the email recipients details below and click on the &apos;Send
        email&apos; button to send the ACP with them
      </p>
      <form>
        {errors.length > 0 && <NewErrorSummary errors={errors} />}
        {sharingByEmailStatus.submitted === SubmitStatusEnum.FAILED && (
          <NewErrorSummary
            errors={[
              {
                id: 'sharingByEmailAPI',
                message: sharingByEmailStatus.message,
              },
            ]}
          />
        )}
        <div className="govuk-form-group">
          <fieldset
            className="govuk-fieldset"
            aria-describedby="asigned-nok-hint"
          >
            <div className="govuk-checkboxes" data-module="govuk-checkboxes">
              <div className="govuk-checkboxes__item">
                <input
                  className="govuk-checkboxes__input"
                  id="asigned-nok"
                  name="asigned-nok"
                  type="checkbox"
                  checked={assignInfo.isChecked}
                  onChange={handleCheckboxChange}
                  disabled={assignInfo.isDisabled}
                />
                <label
                  className="govuk-label govuk-checkboxes__label"
                  htmlFor="asigned-nok"
                >
                  If this person is the assigned Next of Kin for{' '}
                  {residentFullName ? capitalizeName(residentFullName) : '...'}{' '}
                  check the box.
                </label>
              </div>
            </div>
          </fieldset>
          {assignInfo.isDisabled && (
            <WarningText content="A Next of Kin has not been added to this care plan" />
          )}
        </div>

        <div
          className={`govuk-form-group ${errorMessages.fullName ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="sbe-full-name">
            Recipient&apos;s full name
          </label>
          <div className="govuk-hint" id="full-name-hint" />
          {errorMessages.fullName && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errorMessages.fullName}
            </p>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${errorMessages.fullName ? 'govuk-input--error' : ''}`}
            id="sbe-full-name"
            name="name"
            type="text"
            disabled={assignInfo.isChecked}
            aria-describedby={
              errorMessages.fullName ? 'full-name-input-error' : undefined
            }
            autoComplete="name"
            value={recipientInfo.name}
            onChange={e => handleInputChange('name', e.target.value)}
          />
        </div>
        <div
          className={`govuk-form-group ${errorMessages.relationship ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="sbe-relationship">
            Recipient&apos;s relationship
          </label>
          <div className="govuk-hint" id="relationship-hint" />
          {errorMessages.relationship && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errorMessages.relationship}
            </p>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${errorMessages.relationship ? 'govuk-input--error' : ''}`}
            id="sbe-relationship"
            name="relationship"
            type="text"
            disabled={assignInfo.isChecked}
            aria-describedby={
              errorMessages.relationship
                ? 'relationship-input-error'
                : undefined
            }
            autoComplete="relationship"
            value={recipientInfo.relationship}
            onChange={e => handleInputChange('relationship', e.target.value)}
          />
        </div>
        <div
          className={`govuk-form-group ${errorMessages.email ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="sbe-email">
            Recipient&apos;s email address
          </label>
          {errorMessages.email && (
            <p id="email-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errorMessages.email}
            </p>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${errorMessages.email ? 'govuk-input--error' : ''}`}
            id="sbe-email"
            name="email"
            type="email"
            spellCheck={false}
            aria-describedby={
              errorMessages.email ? 'email-input-error' : undefined
            }
            autoComplete="email"
            value={recipientInfo.email}
            onChange={e => handleInputChange('email', e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={
            sharingByEmailStatus.loading || role === Role.TeamMemberViewOnly
          }
          className="govuk-button govuk-!-margin-bottom-0"
          onClick={onFinish}
        >
          Send email
        </button>
      </form>
    </div>
  );
};

export default SharingByEmail;
